import { getCurrentWindow } from '@tauri-apps/api/window';

import { RPA_RECORDER_RESUME_POLLING_EVENT } from '@/app-shell/rpa/recorder/constants';
import { hydratePersistedRecordings } from '@/app-shell/rpa/recorder/lifecycle/hydrate-persisted-recordings';
import { listenRecordingNamed } from '@/app-shell/rpa/recorder/model/recording-events';
import { recorderUploaderClient } from '@/app-shell/rpa/recorder/upload/upload-client.live';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import type { RecordingEntry } from '@heidi/agents/data';
import { settleUploaderWork } from '@heidi/agents/data';
import {
  bridgeErrorKind,
  managedListen,
  onceSafeUnlisten,
  RPA_MAIN_WINDOW_LABEL,
  type ManagedUnlistenFn,
} from '@heidi/plugin-core';
import {
  recorderUploadBridge,
  rpaRecorderBridge,
  type RecorderAnalysisStatusChangedPayload,
  type RecorderUploadBridge,
  type RecorderUploadProgressPayload,
  type RecorderUploadStateChangedPayload,
  type RpaRecorderBridge,
  type SessionId,
} from '@heidi/plugin-rpa';

const resumedSessions = new Set<string>();

export function __resetRpaRecorderLifecycleForTests(): void {
  resumedSessions.clear();
}

export interface RpaRecorderLifecycleDeps {
  readonly isTauri: () => boolean;
  readonly isMainWindow: () => boolean;
  readonly uploadBridge: Pick<
    RecorderUploadBridge,
    | 'init'
    | 'getUploadState'
    | 'subscribeUploadState'
    | 'subscribeUploadProgress'
    | 'subscribeAnalysisStatus'
  >;
  readonly uploaderClient: Pick<
    typeof recorderUploaderClient,
    'uploadNamed' | 'resumePolling'
  >;
  readonly recorderBridge: Pick<RpaRecorderBridge, 'listRecordings'>;
  readonly listenNamed: typeof listenRecordingNamed;
}

export function createDefaultRpaRecorderLifecycleDeps(): RpaRecorderLifecycleDeps {
  return {
    isTauri,
    isMainWindow: () => getCurrentWindow().label === RPA_MAIN_WINDOW_LABEL,
    uploadBridge: recorderUploadBridge,
    uploaderClient: recorderUploaderClient,
    recorderBridge: rpaRecorderBridge,
    listenNamed: listenRecordingNamed,
  };
}

interface RecordingsLifecycleActions {
  readonly addUploading: (
    name: string,
    sessionId: SessionId,
    description: string | null
  ) => void;
  readonly applyUploadState: (payload: RecorderUploadStateChangedPayload) => void;
  readonly applyUploadProgress: (payload: RecorderUploadProgressPayload) => void;
  readonly applyAnalysisStatus: (payload: RecorderAnalysisStatusChangedPayload) => void;
  readonly mergePersisted: (entries: readonly RecordingEntry[]) => void;
}

export function startRpaRecorderLifecycle(
  deps: RpaRecorderLifecycleDeps,
  actions: RecordingsLifecycleActions
): () => void {
  if (!deps.isTauri()) {
    return () => undefined;
  }

  // Tauri events are the primary upload-state path. Promise settlement below is a
  // fallback when the client returns terminal state without a matching emit.
  const offUploadState = deps.uploadBridge.subscribeUploadState(actions.applyUploadState);
  const offUploadProgress = deps.uploadBridge.subscribeUploadProgress(
    actions.applyUploadProgress
  );
  const offAnalysis = deps.uploadBridge.subscribeAnalysisStatus(
    actions.applyAnalysisStatus
  );

  let mainWindowNamedUnlisten: Promise<(() => void) | null> | null = null;
  let pollingCancelled = false;
  const shouldOwnUploadWork = deps.isMainWindow();

  void deps.uploadBridge.init();

  const hydrateDeps = {
    listRecordings: () => deps.recorderBridge.listRecordings(),
    getUploadState: (sessionId: SessionId) => deps.uploadBridge.getUploadState(sessionId),
  };

  void hydratePersistedRecordings(hydrateDeps, actions.mergePersisted);

  let resumePollingUnlisten: Promise<ManagedUnlistenFn> | null = null;

  if (shouldOwnUploadWork) {
    mainWindowNamedUnlisten = deps
      .listenNamed(({ sessionId, name }) => {
        actions.addUploading(name, sessionId, null);
        settleUploaderWork(
          sessionId,
          deps.uploaderClient.uploadNamed(sessionId, name),
          actions.applyUploadState,
          { msg: '[rpa-recorder] Main window failed to start upload' }
        );
      })
      .catch((error: unknown) => {
        logger.warn({
          msg: '[rpa-recorder] recording-named listen failed',
          errorKind: bridgeErrorKind(error),
        });
        return null;
      });

    resumePollingUnlisten = managedListen(RPA_RECORDER_RESUME_POLLING_EVENT, () => {
      void hydratePersistedRecordings(hydrateDeps, actions.mergePersisted)
        .then(() =>
          resumePollingForPersistedSessions(deps, actions, () => pollingCancelled)
        )
        .catch((error: unknown) => {
          logger.warn({
            msg: '[rpa-recorder] resume-persisted-polling hydration failed',
            error: error instanceof Error ? error.message : String(error),
          });
        });
    });

    void resumePollingForPersistedSessions(deps, actions, () => pollingCancelled);
  }

  return () => {
    offUploadState();
    offUploadProgress();
    offAnalysis();
    pollingCancelled = true;
    if (mainWindowNamedUnlisten) {
      // A webview reload can clear Tauri's listener map before this runs.
      void mainWindowNamedUnlisten.then(
        (unlisten) => unlisten && onceSafeUnlisten(unlisten, () => undefined)()
      );
    }
    if (resumePollingUnlisten) {
      // `release` never rejects, so this only sees a failed registration.
      void resumePollingUnlisten
        .then((release) => release())
        .catch((error: unknown) => {
          logger.warn({
            msg: '[rpa-recorder] resume-polling listen failed',
            errorKind: bridgeErrorKind(error),
          });
        });
    }
  };
}

export async function resumePollingForPersistedSessions(
  deps: RpaRecorderLifecycleDeps,
  actions: Pick<RecordingsLifecycleActions, 'applyUploadState'>,
  isCancelled: () => boolean
): Promise<void> {
  let workflows: readonly { readonly id: string }[];
  try {
    workflows = await deps.recorderBridge.listRecordings();
  } catch (error) {
    logger.warn({
      msg: '[rpa-recorder] Lifecycle failed to list persisted recordings',
      error: error instanceof Error ? error.message : String(error),
    });
    return;
  }

  for (const { id: sessionId } of workflows) {
    if (isCancelled()) return;
    await resumeOnePollingSession(deps, actions, sessionId);
  }
}

async function resumeOnePollingSession(
  deps: RpaRecorderLifecycleDeps,
  actions: Pick<RecordingsLifecycleActions, 'applyUploadState'>,
  sessionId: string
): Promise<void> {
  if (resumedSessions.has(sessionId)) return;

  try {
    const state = await deps.uploadBridge.getUploadState(sessionId);
    if (state.stage !== 'polling' || !state.recordingId) return;

    resumedSessions.add(sessionId);

    const work = deps.uploaderClient.resumePolling(sessionId, state.recordingId);
    void work.finally(() => {
      resumedSessions.delete(sessionId);
    });
    settleUploaderWork(sessionId, work, actions.applyUploadState, {
      msg: '[rpa-recorder] Lifecycle failed to resume polling',
    });
  } catch (error) {
    logger.warn({
      msg: '[rpa-recorder] Lifecycle failed to read upload state',
      sessionId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
